// src/App.js
import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandingProvider, useBranding } from './context/BrandingContext';
import Sidebar from './components/Sidebar';
import { Toast, useToast } from './components/UI';
import LeadForm from './components/LeadForm';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import Followups from './pages/Followups';
import AutoResponse from './pages/AutoResponse';
import Import from './pages/Import';
import Settings from './pages/Settings';
import Forms from './pages/Forms';
import Impersonate from './pages/Impersonate';
import PlatformApp from './platform/PlatformApp';
import { getDashboard, getPlatformContext } from './utils/api';
import './index.css';

const PAGE_TITLES = {
  '/': 'Dashboard',
  '/leads': 'All Leads',
  '/followups': 'Follow-up Tracker',
  '/autoresponse': 'Auto-Response',
  '/import': 'Import Leads',
  '/forms': 'Forms & Webhooks',
  '/settings': 'Settings',
};

// The permission each top-level route requires. Settings opens if the user can
// reach any of its admin areas.
const ROUTE_PERMS = {
  '/': ['dashboard.view'],
  '/leads': ['leads.view'],
  '/followups': ['leads.view'],
  '/autoresponse': ['autoresponse.view'],
  '/import': ['leads.import'],
  '/forms': ['forms.view'],
  '/settings': ['settings.manage', 'users.view', 'users.manage', 'roles.manage'],
};

// The first landing route the user actually has access to (used as the
// post-login home and the fallback for unauthorized/unknown paths).
function firstAllowedPath(canAny) {
  const order = ['/', '/leads', '/followups', '/forms', '/autoresponse', '/import', '/settings'];
  return order.find(p => canAny(ROUTE_PERMS[p])) || null;
}

// Blocks direct-URL access to a page the user's role can't see, redirecting to
// their first accessible page. The backend still enforces on every request;
// this just keeps the UI honest.
function Guard({ perms, children }) {
  const { canAny } = useAuth();
  if (!canAny(perms)) {
    const home = firstAllowedPath(canAny);
    return home ? <Navigate to={home} replace /> : <NoAccess />;
  }
  return children;
}

function NoAccess() {
  return (
    <div style={{ padding: 48, textAlign: 'center', color: 'var(--gray-400)' }}>
      <i className="ti ti-lock" style={{ fontSize: 40, display: 'block', marginBottom: 12 }} />
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--gray-600)' }}>No access</div>
      <div style={{ fontSize: 13, marginTop: 6 }}>You don't have permission to view this page. Contact your administrator.</div>
    </div>
  );
}

function AppShell() {
  const { user, loading, canAny, signOut } = useAuth();
  const { branding } = useBranding();
  const { msg, show, clear } = useToast();
  const [pendingFU, setPendingFU] = useState(0);
  const [totalLeads, setTotalLeads] = useState(0);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const refreshStats = useCallback(() => {
    if (user) {
      getDashboard()
        .then(r => { setPendingFU(r.data.stats.pending_fu); setTotalLeads(r.data.stats.total); })
        .catch(() => {});
    }
  }, [user]);

  useEffect(() => { refreshStats(); }, [refreshStats, location.pathname]);

  useEffect(() => {
    const handler = () => setShowLeadForm(true);
    window.addEventListener('open-new-lead', handler);
    return () => window.removeEventListener('open-new-lead', handler);
  }, []);

  const title = Object.keys(PAGE_TITLES).find(k => k === location.pathname) ? PAGE_TITLES[location.pathname] : 'Lead Detail';

  // Redeeming an impersonation code must work regardless of what session (if
  // any) already exists in this browser — it deliberately replaces it. Handled
  // before the loading/user gates so it isn't bounced to /login.
  if (location.pathname === '/impersonate') return <Impersonate />;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-400)' }}>
        Loading {branding.brand_name} CRM...
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const impersonating = !!user.impersonated_by;

  return (
    <>
      {/* An impersonated session must never be invisible — this is always on top. */}
      {impersonating && (
        <div style={{
          height: 38, background: '#7c2d12', color: '#fff', display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: 10, fontSize: 12.5, fontWeight: 600, padding: '0 16px',
        }}>
          <i className="ti ti-eye" />
          <span>
            Impersonating <strong>{user.name}</strong> ({user.email}) — this session is logged and expires in 1 hour.
          </span>
          <button
            onClick={signOut}
            style={{
              background: 'rgba(255,255,255,0.16)', color: '#fff', border: 'none', borderRadius: 6,
              padding: '3px 10px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)',
            }}
          >
            Exit
          </button>
        </div>
      )}
    <div className="app" style={impersonating ? { height: 'calc(100vh - 38px)' } : undefined}>
      <Sidebar pendingFU={pendingFU} totalLeads={totalLeads} />
      <div className="main">
        <div className="topbar">
          <div className="topbar-title">{title}</div>
          <div className="topbar-actions">
            {canAny('leads.create') && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowLeadForm(true)}>
                <i className="ti ti-plus" /> New Lead
              </button>
            )}
          </div>
        </div>
        <div className="content">
          <Routes>
            <Route path="/" element={<Guard perms={ROUTE_PERMS['/']}><Dashboard /></Guard>} />
            <Route path="/leads" element={<Guard perms={ROUTE_PERMS['/leads']}><Leads toast={show} /></Guard>} />
            <Route path="/leads/:id" element={<Guard perms={ROUTE_PERMS['/leads']}><LeadDetail toast={show} /></Guard>} />
            <Route path="/followups" element={<Guard perms={ROUTE_PERMS['/followups']}><Followups /></Guard>} />
            <Route path="/autoresponse" element={<Guard perms={ROUTE_PERMS['/autoresponse']}><AutoResponse toast={show} /></Guard>} />
            <Route path="/import" element={<Guard perms={ROUTE_PERMS['/import']}><Import toast={show} /></Guard>} />
            <Route path="/forms" element={<Guard perms={ROUTE_PERMS['/forms']}><Forms toast={show} /></Guard>} />
            <Route path="/settings" element={<Guard perms={ROUTE_PERMS['/settings']}><Settings toast={show} /></Guard>} />
            <Route path="*" element={<Navigate to={firstAllowedPath(canAny) || '/'} replace />} />
          </Routes>
        </div>
      </div>
      <Toast message={msg} onClose={clear} />
      <LeadForm
        open={showLeadForm}
        onClose={() => setShowLeadForm(false)}
        lead={null}
        onSaved={(saved) => {
          refreshStats();
          if (location.pathname !== '/leads') navigate('/leads');
          else window.dispatchEvent(new CustomEvent('leads-refresh'));
        }}
        toast={show}
      />
    </div>
    </>
  );
}

export default function App() {
  // One React build serves every host. Ask the backend whether this host is the
  // superadmin dashboard host; if so, render the platform control panel instead
  // of the tenant CRM. `null` = still deciding (avoids a flash of the wrong UI).
  const [isPlatformHost, setIsPlatformHost] = useState(null);

  useEffect(() => {
    getPlatformContext()
      .then(r => setIsPlatformHost(!!r.data.is_platform_admin_host))
      .catch(() => setIsPlatformHost(false));
  }, []);

  if (isPlatformHost === null) return null;
  if (isPlatformHost) return <PlatformApp />;

  return (
    <BrowserRouter>
      <BrandingProvider>
        <AuthProvider>
          <AppShell />
        </AuthProvider>
      </BrandingProvider>
    </BrowserRouter>
  );
}
